// src/features/learner/components/ContentRenderers/TypeInRenderer.js
import React from 'react';
import AudioPlayer from '../../../../features/MediaLibrary/AudioPlayer';

export const TypeInRenderer = ({ 
    unit, 
    userAnswers, 
    showFeedback, 
    onAnswerSelect, 
    onCheckAnswer,
    onMarkCompleted,
    onResetUnit
}) => {
    const content = unit.content || {};
    const showFeedbackForUnit = showFeedback[unit.id];
    const userAnswer = userAnswers[unit.id] || [];

    const questionText = content.text || '';
    const answers = content.answers || [];

    // Calculate correctness once at render time
    const allCorrect = answers.every((answer, index) => {
        const userInput = userAnswer[index] || '';
        const correctAnswers = [
            answer.text,
            ...(answer.alternatives ? answer.alternatives.split(',').map(a => a.trim()) : [])
        ];
        return correctAnswers.some(correct => correct.toLowerCase() === userInput.toLowerCase());
    });
    const handleTypeInChange = (inputIndex, value) => {
        if (showFeedbackForUnit) return;
        const newAnswer = [...userAnswer];
        newAnswer[inputIndex] = value;
        if (onAnswerSelect) {
            onAnswerSelect(unit.id, newAnswer);
        }
    };

    const handleCheckAnswer = () => {
        if (onMarkCompleted) onMarkCompleted(unit.id, null, allCorrect ? 'correct' : 'wrong');
        if (onCheckAnswer) onCheckAnswer(unit.id);
    };

    const renderQuestionWithNumberedBlanks = () => {
        // First extract [AUDIO:id:name] tokens so they never become answer blanks
        const audioTokenRegex = /\[AUDIO:([^:\]]+):([^\]]+)\]/g;
        const segments = [];
        let lastIndex = 0;
        let match;
        while ((match = audioTokenRegex.exec(questionText)) !== null) {
            if (match.index > lastIndex) {
                segments.push({ type: 'text', value: questionText.slice(lastIndex, match.index) });
            }
            segments.push({ type: 'audio', id: match[1], name: match[2] });
            lastIndex = match.index + match[0].length;
        }
        if (lastIndex < questionText.length) {
            segments.push({ type: 'text', value: questionText.slice(lastIndex) });
        }

        let inputIndex = 0;
        const elements = [];
        segments.forEach((segment, segIndex) => {
            if (segment.type === 'audio') {
                elements.push(
                    <span key={`audio-${segIndex}`} style={{ display: 'inline-block', margin: '8px 4px', verticalAlign: 'middle' }}>
                        <AudioPlayer audioId={segment.id} showFileName={false} />
                    </span>
                );
            } else {
                const parts = segment.value.split(/\[(.*?)\]/);
                parts.forEach((part, partIndex) => {
                    if (partIndex % 2 === 0) {
                        elements.push(<span key={`text-${segIndex}-${partIndex}`} dangerouslySetInnerHTML={{ __html: part }} />);
                    } else {
                        const currentInputIndex = inputIndex++;
                        elements.push(
                            <span key={`blank-${segIndex}-${partIndex}`} className="type-in-blank-wrapper">
                                <span className="blank-number">({currentInputIndex + 1})</span>
                            </span>
                        );
                    }
                });
            }
        });
        return elements;
    };

    const isFormValid = userAnswer.length > 0 && !userAnswer.some(a => !a?.trim());

    return (
        <div className="unit-content type-in-content">
                        <div className="type-in-question">
                <div className="question-text">
                    {renderQuestionWithNumberedBlanks()}
                </div>
            </div>

            <div className="fill-blanks-section">
                <h3>Fill in the blanks:</h3>
                <div className="blanks-inputs">
                    {answers.map((answer, index) => (
                        <div key={index} className="blank-input-row">
                            <label className="blank-label">Blank {index + 1}:</label>
                            <input 
                                type="text" 
                                className="type-in-input" 
                                value={userAnswer[index] || ''}
                                onChange={(e) => handleTypeInChange(index, e.target.value)}
                                placeholder="Type your answer here"
                                disabled={showFeedbackForUnit}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {!showFeedbackForUnit ? (
                <button
                    className="check-answer-button"
                    onClick={handleCheckAnswer}
                    disabled={!isFormValid}
                >
                    Check Answer
                </button>
            ) : (
                <div className="feedback-container">
                    <div className="feedback-header">Results</div>
                    <div className="feedback-text">
                        {answers.map((answer, index) => {
                            const userInput = userAnswer[index] || '';
                            const correctAnswers = [
                                answer.text, 
                                ...(answer.alternatives ? answer.alternatives.split(',').map(a => a.trim()) : [])
                            ];
                            const isCorrect = correctAnswers.some(correct => 
                                correct.toLowerCase() === userInput.toLowerCase()
                            );
                            
                            return (
                                <div key={index} className={`answer-result ${isCorrect ? 'correct' : 'incorrect'}`}>
                                    <strong>Blank {index + 1}:</strong>{' '}
                                    Your answer: {userInput}{' '}
                                    {isCorrect ? ' ✓' : ` ✗ (Correct: ${correctAnswers.join(', ')})`}
                                    {answer.feedback && <div className="answer-feedback">{answer.feedback}</div>}
                                </div>
                            );
                        })}
                        {content.generalFeedback && (
                            <div className="general-feedback">{content.generalFeedback}</div>
                        )}
                    </div>
                    {!answers.every((answer, index) => {
                        const userInput = userAnswer[index] || '';
                        const correctAnswers = [
                            answer.text,
                            ...(answer.alternatives ? answer.alternatives.split(',').map(a => a.trim()) : [])
                        ];
                        return correctAnswers.some(c => c.toLowerCase() === userInput.toLowerCase());
                    }) && onResetUnit && (
                        <div style={{ textAlign: 'center', marginTop: '12px' }}>
                            <button
                                className="reset-button"
                                onClick={() => onResetUnit(unit.id)}
                                title="Try again"
                                style={{
                                    padding: '8px 12px', fontSize: '18px',
                                    backgroundColor: '#6c757d', color: 'white',
                                    border: 'none', borderRadius: '50%', cursor: 'pointer',
                                    width: '40px', height: '40px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    margin: '0 auto', transition: 'all 0.2s ease',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                🔄
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};